import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { TypingEffect } from '@/components/ui/TypingEffect';
import { useReducedMotion } from '@/hooks/useReducedMotion';

interface EnhancedHeadlineProps {
  className?: string;
}

export const EnhancedHeadline: React.FC<EnhancedHeadlineProps> = ({
  className = '',
}) => {
  const [firstLineComplete, setFirstLineComplete] = useState(false);
  const [secondLineComplete, setSecondLineComplete] = useState(false);
  const prefersReducedMotion = useReducedMotion();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  if (prefersReducedMotion) {
    return (
      <h1 className={`${className} relative`} style={{ minHeight: '160px', height: '160px' }}>
        <div>Stop Guessing Tuesday</div>
        <div className="block mt-2">
          <span className="font-bold bg-gradient-to-r from-emerald-600 via-cyan-600 to-blue-600 bg-clip-text text-transparent">Lottery Payments</span>
        </div>
      </h1>
    );
  }

  return (
    <motion.h1
      className={`${className} relative`}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      style={{ minHeight: '160px', height: '160px' }} // Fixed height to prevent jumping
    >
      {/* Full text with reserved space */}
      <div className="leading-tight">
        <div>
          <TypingEffect
            text="Stop Guessing Tuesday"
            delay={70}
            speed={30}
            className="inline-block bg-gradient-to-r from-slate-900 via-emerald-800 to-slate-900 bg-clip-text text-transparent"
            onComplete={() => setFirstLineComplete(true)}
          />
        </div>
        <div className="block mt-2">
          {(firstLineComplete || prefersReducedMotion) ? (
            <TypingEffect
              text="Lottery Payments"
              delay={50}
              speed={30}
              className="inline-block font-bold bg-gradient-to-r from-emerald-600 via-cyan-600 to-blue-600 bg-clip-text text-transparent animate-gradient-shift"
              onComplete={() => setSecondLineComplete(true)}
            />
          ) : (
            <span className="inline-block font-bold bg-gradient-to-r from-emerald-600 via-cyan-600 to-blue-600 bg-clip-text text-transparent opacity-0">
              Lottery Payments
            </span>
          )}
        </div>
      </div>

      {/* Floating particles around the text */}
      {(secondLineComplete || prefersReducedMotion) && (
        <>
          {Array.from({ length: 6 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-gradient-to-r from-emerald-400 to-cyan-400 rounded-full shadow-sm"
              style={{
                left: `${20 + i * 15}%`,
                top: `${30 + (i % 2) * 40}%`,
              }}
              animate={{
                y: [0, -10, 0],
                opacity: [0.3, 0.8, 0.3],
                scale: [1, 1.2, 1],
              }}
              transition={{
                duration: 2 + i * 0.5,
                repeat: Infinity,
                ease: "easeInOut",
                delay: i * 0.2,
              }}
            />
          ))}
        </>
      )}
    </motion.h1>
  );
};
